import {
  type DiscoverChatbotProfileOverlayPreview,
  type DiscoverChatbotProfileOverlayProfile,
  type DiscoverChatbotProfileOverlayViewProps,
} from "@next-platform/client-common-core";
import { Button, ButtonColorVariant, ButtonSize, ButtonVariant, IconVariants, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import React, { useId } from "react";

import AccessibleDialog from "@/components/common/AccessibleDialog/AccessibleDialog";
import TagSuggestion from "@/components/TagSuggestion/TagSuggestion";

import DiscoverChatbotArtwork from "./DiscoverChatbotArtwork";
import DiscoverChatbotProfileOverlayHeader from "./DiscoverChatbotProfileOverlayHeader";
import DiscoverChatbotProfileOverlaySections from "./DiscoverChatbotProfileOverlaySections";
import DiscoverChatbotProfileOverlayStats from "./DiscoverChatbotProfileOverlayStats";
import DiscoverChatbotProfileOverlayStatus from "./DiscoverChatbotProfileOverlayStatus";

const TEST_ID = "DiscoverChatbotProfileOverlayView";

// -- Class Name Constants --
const PANEL_CLASS_NAME = "flex flex-col gap-5 rounded-lg bg-white p-2.5 dark:!bg-gray-3" + " max-w-[900px] w-[92vw] max-h-[700px] h-[86vh]";

const OUTER_CONTAINER_CLASS = "flex flex-col sm:flex-row items-start gap-5 w-full h-full overflow-auto sm:overflow-hidden";
const CONTENT_CONTAINER_CLASS = "w-full flex-1 flex flex-col sm:h-full sm:overflow-hidden";
const SCROLLER_CONTAINER_CLASS = "flex-1 flex flex-col h-full sm:overflow-auto gap-2.5 pb-8";
const LOREBOOK_BUTTON_CLASS = "w-fit";

type OverlayHeaderData = {
  readonly name: string;
  readonly avatarUrl?: string;
  readonly isAvatarBlurred: boolean;
  readonly isRecommended: boolean;
};

type OverlayProfileContentProps = Pick<DiscoverChatbotProfileOverlayViewProps, "profile" | "labels" | "onLorebookPress" | "canSuggestTag"> & {
  readonly testID: string;
};

function readHeaderData(
  profile: DiscoverChatbotProfileOverlayProfile | null,
  preview: DiscoverChatbotProfileOverlayPreview | null,
): OverlayHeaderData {
  const source = profile || preview;

  return {
    name: source?.name || "",
    avatarUrl: source?.avatarUrl,
    isAvatarBlurred: Boolean(source?.isAvatarBlurred),
    isRecommended: Boolean(source?.isRecommended),
  };
}

const OverlayProfileContent = ({ profile, labels, testID, onLorebookPress, canSuggestTag }: OverlayProfileContentProps) => {
  if (!profile) return null;

  const { lorebook } = profile;

  return (
    <>
      <DiscoverChatbotProfileOverlayStats stats={profile.stats} labels={labels} testID={TEST_ID} />
      {profile.description ? <Text variant={TextVariants.LABEL_MEDIUM_REGULAR}>{profile.description}</Text> : null}
      {profile ? <TagSuggestion id={profile.characterId} tags={profile.tags} suggestDisabled={!canSuggestTag} /> : null}
      {lorebook ? (
        <Button
          testID={`${testID}-LorebookButton`}
          variant={lorebook.isInteractionDisabled ? ButtonVariant.OUTLINED : ButtonVariant.CONTAINED}
          color={lorebook.isInteractionDisabled ? ButtonColorVariant.DEFAULT : ButtonColorVariant.ALERT}
          iconStart={lorebook.isInteractionDisabled ? IconVariants.BOOK_LOCK : IconVariants.BOOK_TEXT}
          disabled={lorebook.isInteractionDisabled}
          onClick={onLorebookPress}
          className={LOREBOOK_BUTTON_CLASS}
        >
          {lorebook.isInteractionDisabled ? labels.privateLorebook : lorebook.name}
        </Button>
      ) : null}

      <DiscoverChatbotProfileOverlaySections profile={profile} labels={labels} />
    </>
  );
};

const SCROLLER_STYLE = { scrollbarWidth: "thin", scrollbarColor: "#2b2b2e transparent" } as const;

const DiscoverChatbotProfileOverlayView = ({
  isOpen,
  status,
  preview,
  profile,
  canChat,
  canFavorite,
  isFavorite,
  canShare,
  canOpenCreator,
  canBlockCreator,
  canReport,
  canLessLikeThis,
  isFavoritePending,
  labels,
  onClose,
  onChatNow,
  onFavorite,
  onShare,
  onCreatorPress,
  onLorebookPress,
  onBlockCreator,
  onReport,
  onLessLikeThis,
  onRetry,
  canSuggestTag,
}: DiscoverChatbotProfileOverlayViewProps) => {
  const headingId = useId();
  const { name, avatarUrl, isAvatarBlurred, isRecommended } = readHeaderData(profile, preview);
  const isLoading = status === "loading";
  // Memoize scrollerStyle objects to avoid new object per render
  const scrollerStyle = SCROLLER_STYLE;

  return (
    <AccessibleDialog isOpen={isOpen} onClose={onClose} labelledBy={headingId} className={PANEL_CLASS_NAME} data-testid={TEST_ID}>
      <div className={OUTER_CONTAINER_CLASS} style={scrollerStyle}>
        <DiscoverChatbotArtwork name={name} isAvatarBlurred={isAvatarBlurred} avatarUrl={avatarUrl} testID={`${TEST_ID}-Avatar`} />
        <div className={CONTENT_CONTAINER_CLASS}>
          <div className={SCROLLER_CONTAINER_CLASS} style={scrollerStyle}>
            <DiscoverChatbotProfileOverlayHeader
              labels={labels}
              onClose={onClose}
              canFavorite={canFavorite}
              isFavorite={isFavorite}
              canShare={canShare}
              canBlockCreator={canBlockCreator}
              canReport={canReport}
              canLessLikeThis={canLessLikeThis}
              isFavoritePending={isFavoritePending}
              onFavorite={onFavorite}
              onShare={onShare}
              onBlockCreator={onBlockCreator}
              onReport={onReport}
              onLessLikeThis={onLessLikeThis}
              testID={TEST_ID}
              name={name}
              canOpenCreator={canOpenCreator}
              onCreatorPress={onCreatorPress}
              creatorUsername={profile?.creatorUsername}
              headingId={headingId}
              isRecomended={isRecommended}
              loading={isLoading}
            />
            <DiscoverChatbotProfileOverlayStatus status={status} labels={labels} onRetry={onRetry} testID={TEST_ID} />
            <OverlayProfileContent
              profile={profile}
              labels={labels}
              onLorebookPress={onLorebookPress}
              canSuggestTag={canSuggestTag}
              testID={TEST_ID}
            />
          </div>
          <Button
            iconStart={IconVariants.MESSAGE_MORE}
            variant={ButtonVariant.CONTAINED}
            disabled={!canChat}
            size={ButtonSize.LARGE}
            onClick={onChatNow}
            data-testid={`${TEST_ID}-ChatNowButton`}
          >
            {labels.chatNow}
          </Button>
        </div>
      </div>
    </AccessibleDialog>
  );
};

export default React.memo(DiscoverChatbotProfileOverlayView);
